'use client';

import dynamic from 'next/dynamic';
import Link from '~/(tenant)/_components/tenant-link';

import { PersonalAccountDropdown } from '@kit/accounts/personal-account-dropdown';
import { useSignOut } from '@kit/supabase/hooks/use-sign-out';
import { JWTUserData } from '@kit/supabase/types';
import { If } from '@kit/ui/if';
import { Trans } from '@kit/ui/trans';

import { UserAccountDropdown } from '~/(tenant)/_components/user-account-dropdown';
import featuresFlagConfig from '~/config/feature-flags.config';
import pathsConfig from '~/config/paths.config';
import { useSession, useUserRole, useTeamAccount } from '~/lib/hooks/use-header-auth';

const ModeToggle = dynamic(() =>
  import('@kit/ui/mode-toggle').then((mod) => ({
    default: mod.ModeToggle,
  })),
);

const MobileModeToggle = dynamic(() =>
  import('@kit/ui/mobile-mode-toggle').then((mod) => ({
    default: mod.MobileModeToggle,
  })),
);

const paths = {
  home: pathsConfig.app.home,
  bookCourt: '/book',
};

const features = {
  enableThemeToggle: featuresFlagConfig.enableThemeToggle,
};

interface BallersjoHeaderAccountSectionProps {
  onNavigate?: () => void;
  variant?: 'light' | 'dark';
}

export function BallersjoHeaderAccountSection({
  onNavigate,
  variant = 'light',
}: BallersjoHeaderAccountSectionProps) {
  const session = useSession();
  const userRole = useUserRole(session.data?.user?.id);
  const teamAccount = useTeamAccount(session.data?.user?.id);
  const signOut = useSignOut({
    onSuccess: () => window.location.reload(),
  });

  const primaryBtn =
    variant === 'light'
      ? 'inline-flex h-10 items-center justify-center bg-black px-5 text-[12px] font-semibold tracking-[0.12em] uppercase text-white transition-colors duration-200 hover:bg-black/85'
      : 'inline-flex h-10 items-center justify-center bg-white px-5 text-[12px] font-semibold tracking-[0.12em] uppercase text-black transition-colors duration-200 hover:bg-white/90';

  const ghostBtn =
    variant === 'light'
      ? 'inline-flex h-10 items-center justify-center border border-black px-5 text-[12px] font-semibold tracking-[0.12em] uppercase text-black transition-colors duration-200 hover:bg-black hover:text-white'
      : 'inline-flex h-10 items-center justify-center border border-white px-5 text-[12px] font-semibold tracking-[0.12em] uppercase text-white transition-colors duration-200 hover:bg-white hover:text-black';

  if (session.data) {
    const role = userRole.data ?? 'guest';

    if (role === 'owner' || role === 'member') {
      return (
        <div className="animate-in fade-in flex items-center gap-x-3 duration-500">
          <div className="hidden md:flex">
            <If condition={features.enableThemeToggle}>
              <ModeToggle />
            </If>
          </div>

          <Link href={paths.bookCourt} onClick={onNavigate} className={primaryBtn}>
            <Trans i18nKey="common:bookCourt" defaults="Book Now" />
          </Link>

          <Link
            href={
              teamAccount.data
                ? `/home/${teamAccount.data.slug}/dashboard`
                : '/home/settings'
            }
            onClick={onNavigate}
            className={`hidden md:inline-flex ${ghostBtn.replace('inline-flex ', '')}`}
          >
            <Trans i18nKey="common:adminPanel" defaults="Admin Panel" />
          </Link>

          <PersonalAccountDropdown
            showProfileName={false}
            paths={paths}
            features={features}
            user={session.data.user as unknown as JWTUserData}
            signOutRequested={() => signOut.mutateAsync()}
            className="z-50"
          />
        </div>
      );
    }

    if (role === 'user') {
      return (
        <div className="animate-in fade-in flex items-center gap-x-3 duration-500">
          <div className="hidden md:flex">
            <If condition={features.enableThemeToggle}>
              <ModeToggle />
            </If>
          </div>

          <Link href={paths.bookCourt} onClick={onNavigate} className={primaryBtn}>
            <Trans i18nKey="common:bookCourt" defaults="Book Now" />
          </Link>

          <UserAccountDropdown
            showProfileName={false}
            features={features}
            user={session.data.user as unknown as JWTUserData}
            signOutRequested={() => signOut.mutateAsync()}
            className="z-50"
          />
        </div>
      );
    }
  }

  return (
    <div className="animate-in fade-in flex items-center gap-x-3 duration-500">
      <div className="hidden md:flex">
        <If condition={features.enableThemeToggle}>
          <ModeToggle />
        </If>
      </div>

      <div className="md:hidden">
        <If condition={features.enableThemeToggle}>
          <MobileModeToggle />
        </If>
      </div>

      <Link href={paths.bookCourt} onClick={onNavigate} className={primaryBtn}>
        <Trans i18nKey="common:bookCourt" defaults="Book Now" />
      </Link>

      <a href={pathsConfig.auth.signIn} onClick={onNavigate} className={ghostBtn}>
        <Trans i18nKey="auth:signIn" defaults="Sign In" />
      </a>
    </div>
  );
}
